import _ from 'lodash'
import wordlist, { solutionWordList } from '../data/words'
import getAllMutators, { getRandomMutator, Mutator, willDoAnything } from './mutators'
import seedrandom from 'seedrandom'
import { getRandomTheme, getTheme, Theme } from '../data/themedWords'
import { log } from '../utils/logging'
import { sampleN, shuffle } from '../utils/rand'
import { generateMutatorSequence } from './generator/generateMutatorSequence'
export interface Letter {
    value: string,
    correctValue: string|null,
    key: number|string
    isGhost?: true // Only used for previewing
}

export interface Word {
    correctValue: string,
    value: Letter[],
    mutationCount: number // The number of mutations will resulted in a change to this word
}

export interface GameMutator {
    mutator: Mutator,
    isCorrect: boolean // True if the mutator is used in the solution
    correctOrder: number|null // The order in which the mutator is used in the solution
    used: boolean
}
export interface Solution {
    mutators: Mutator[]
}

export interface GameState {
    words: Word[]
    gameMutators: GameMutator[]
    solutions: Solution[]
    mutationStack: Mutator[] //The current list of applied mutators, in order
    customWords?: string[]
}
export type GameStateOptions = {
    solutionMutators?: Mutator[],
    redHerringMutators?: Mutator[]
    numberOfWords: number
    solutionLength: number,
    maxDifferentSolutions?: number,
    allowAlternateMutatorsInSolutions?: boolean
    onError?: (e: unknown)=>void
    requiresAdditive: boolean,
    requiredMutator?: Mutator,
    requiredWords?: string[]
} & (
    {initialWords: string[], theme?: undefined, randomTheme?:false} // Initial words are provided
    |  {initialWords?: undefined, theme: Theme, randomTheme?:false} // Theme is provided
    |  {initialWords?: undefined, theme?: undefined, randomTheme:true} // Random theme
    |  {initialWords?: undefined, theme?: undefined, randomTheme?:false} // No theme or initial words
)

export function newGameState(seed: string, opts: GameStateOptions): GameState {
    seedrandom(seed, {global: true})
    const MAX_ATTEMPTS = 1000;
    const MAX_THEMED_ATTEMPTS = 100;
    const MAX_REQUIRED_MUTATOR_ATTEMPTS = 150;

    for(let i = 0; i < MAX_ATTEMPTS; i++){
        try{
            let themedWords: string[] | undefined = undefined;
            let theme: Theme | undefined = undefined;

            if (i < MAX_THEMED_ATTEMPTS) {
                if (opts.randomTheme) {
                    [themedWords, theme] = getRandomTheme();
                } else if (opts.theme) {
                    theme = opts.theme;
                    themedWords = getTheme(theme);
                }
            }

            const result = tryNewGameState({
                ...opts,
                initialWords: opts.initialWords ?? themedWords,
                requiredMutator: i < MAX_REQUIRED_MUTATOR_ATTEMPTS ? opts.requiredMutator : undefined
            })
            log({
                theme,
                solution: result.solutions[0].mutators.map(m=>m.name),
                words: result.words.map(w=>w.correctValue),
            })
            return result
        }catch(e){
            // Try again
            opts?.onError?.({error:e, attempt:i})
        }
    }
    throw new Error('Failed to generate game state')
}


type TryNewGameStateOptions= Omit<GameStateOptions, 'initialWords'> & {initialWords?: string[]}
export function tryNewGameState({
    solutionMutators, 
    redHerringMutators,
    initialWords: customWords,
    numberOfWords,
    solutionLength,
    maxDifferentSolutions,
    allowAlternateMutatorsInSolutions,
    requiresAdditive,
    requiredMutator,
    requiredWords
}: TryNewGameStateOptions): GameState {
    let words: Word[] = []
    const STARTING_WORDS = 200
    const MAX_REDHERRING_ATTEMPTS = 100;
    const MAX_ATTEMPTS = getAllMutators().length*2;
    const MUTATORS = 12

    const customAndRequiredWords =  [...customWords??[], ...requiredWords??[]]

    let initialWords = customWords ?? sampleN(solutionWordList, STARTING_WORDS)
    if (requiredWords) {
        initialWords.push(...requiredWords)
        initialWords = Array.from(new Set(initialWords))
    }

    initialWords.forEach(correctValue=>{
        const letters:Letter[] = correctValue.split('').map((letter, i)=>({
            correctValue: letter,
            value: letter,
            key: i
        }))
        words.push({
            correctValue,
            value: letters,
            mutationCount: 0
        })
    })

    const { correctMutators, words: wordsPostMutators } = generateMutatorSequence({
        maxAttempts: MAX_ATTEMPTS,
        requiredMutator,
        solutionLength,
        solutionMutators,
        words,
        requiredWords,
        numberOfWords
    });
    words = wordsPostMutators;
    

    if(requiresAdditive){
        const anyAdditiveMutators = correctMutators.some(m=>m.additive)
        if(!anyAdditiveMutators){
            throw new Error('No additive mutators')
        }
    }


    let gameMutators: GameMutator[] = correctMutators.reverse().map((mutator, i)=>({
        mutator,
        isCorrect: true,
        used: false,
        correctOrder: i
    }))
    let redHerringAttempts = 0;

    for (let i = 0; i < MUTATORS - solutionLength; i++) {
        if (redHerringAttempts >= MAX_REDHERRING_ATTEMPTS) {
            throw new Error('Failed to select red herrings');
        }

        const mutator = redHerringMutators ? redHerringMutators.pop() : getRandomMutator({ useRedHerringWeights: true }); // Get a red herring
        if (!mutator) break;

        if (gameMutators.some(gm => gm.mutator === mutator)) {
            i--;
            redHerringAttempts++;
            continue;
        }

        const modifyCount = words.filter(w => willDoAnything(mutator, w)).length;
        if (modifyCount <= numberOfWords / 2) {
            i--;
            redHerringAttempts++;
            continue;
        }

        gameMutators.push({
            mutator,
            isCorrect: false,
            used: false,
            correctOrder: null
        });
    }
    gameMutators = shuffle(gameMutators)

    words = shuffle(words)
    words = _.sortBy(words, w=> [(requiredWords && requiredWords.includes(w.correctValue) && requiredWords.indexOf(w.correctValue)) ,-1 * w.mutationCount])
    words = words.slice(0, numberOfWords)
    const solutions = findAllSolutions(words, gameMutators, solutionLength, customAndRequiredWords)
    if(solutions.length === 0){
        throw new Error('No solutions')
    }
    if(maxDifferentSolutions && solutions.length > maxDifferentSolutions){
        throw new Error('Too many solutions')
    }
    if (solutions.some(solution=>solution.length < solutionLength)){
        throw new Error('Short solution')
    }
    if(!allowAlternateMutatorsInSolutions && solutions.some(solution=>solution.some((m)=>!correctMutators.includes(m)))){
        throw new Error('Alternate mutators in solutions')
    }

    return {
        words,
        solutions: solutions.map(mutators=>({mutators})),
        mutationStack: [],
        gameMutators: gameMutators,
        customWords: customAndRequiredWords
    }
}

export enum MutationResult {
    Correct = '🟩', // Mutation matches a solution
    WrongPlace = '🟨', // Mutation is in a solution but in the wrong place
    Incorrect = '⬜', // Mutation is not in a solution
}
export function applyMutator(gameState: GameState, mutator: Mutator): [GameState, MutationResult] {
    const words = gameState.words.map(w=>{
        return mutator.apply(w)
    })
    const mutationStack = [...gameState.mutationStack, mutator]
    const newGameState = {
        ...gameState,
        mutators: gameState.gameMutators.map(gm=>({
            ...gm,
            used: gm.used || gm.mutator === mutator,
        })),
        words,
        mutationStack
    }
    
    const isCorrect = gameState.solutions.some(solution=>{
        return mutationStack.every((m, i)=>{
            return solution.mutators[i] === m
        })
    })

    if (isCorrect){
        return [newGameState, MutationResult.Correct]
    }

    const isWrongPlace = gameState.solutions.some(solution=>{
        return solution.mutators.some(m=> m === mutator)
    })

    if (isWrongPlace){
        return [newGameState, MutationResult.WrongPlace]
    }

    return [newGameState, MutationResult.Incorrect]
}

export function isGameSolved(words: Word[], customWords:string[]): boolean {
    return words.every(w=>isValidWord(w,customWords))
} 

function findAllSolutions(words: Word[], gameMutators: GameMutator[], maxDepth:number, customWords: string[]): Mutator[][] {
    const solutions: Mutator[][] = []
    const findSolutions = (words: Word[], gameMutators: GameMutator[], depth: number, currentSolution: Mutator[], customWords:string[])=>{
        if (depth > maxDepth){
            return
        }
        if (isGameSolved(words, customWords)){
            solutions.push([...currentSolution])
            return
        }
        const mutators = gameMutators.filter(gm=>!gm.used)
        for (let i = 0; i < mutators.length; i++){
            const mutator = mutators[i]
            const newWords = words.map(w=>mutator.mutator.apply(w))
            findSolutions(newWords, gameMutators.map(gm=>({
                ...gm,
                used: gm.used || gm === mutator
            })), depth + 1, [...currentSolution, mutator.mutator], customWords)
        }
    }
    findSolutions(words, gameMutators, 0, [], customWords)
    return solutions
}


export function isValidWord(word: Word, customWords: string[]): boolean {
    const string = word.value.map(l=>l.value).join('')
    if(wordlist.includes(string)){
        return true
    }
    if(customWords.includes(string)){
        return true
    }
    return false
}